import { pb } from "@/lib/pocketbase";

export interface GSCPerformanceResponse {
  clicks: number;
  impressions: number;
  ctr: number;
  avgPosition: number;
  keywords: number;
  clicksGrowth: number; // percent growth vs previous period
  impressionsGrowth: number; // percent growth vs previous period
}

// Period type for Analytics dashboard
export type GSCPeriod = "7d" | "30d" | "90d" | "custom";

// Type for Indexed Pages
export interface IndexedPagesResponse {
  indexedPages: number;
  pages: string[];
}

// Type for Backlink
export interface BacklinkData {
  source: string;
  url: string;
  target: string;
  anchor: string;
  authority: number;
  firstSeen: string;
}


// --- Over Time ---
export interface GSCOverTimeData {
  date: string;
  clicks: number;
  impressions: number;
}

/**
 * Fetch daily clicks and impressions from backend GSC endpoint
 * @param brandId Brand ID
 * @param startDate YYYY-MM-DD
 * @param endDate YYYY-MM-DD
 * @returns Array of GSCOverTimeData
 */
export async function fetchGSCTrafficOverTime(
  brandId: string,
  startDate: string,
  endDate: string,
): Promise<GSCOverTimeData[]> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/traffic-over-time`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId, startDate, endDate }),
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch GSC Traffic Over Time");
  }
  return res.json();
}

// --- Top Pages ---
export interface GSCTopPage {
  pageUrl: string;
  clicks: number;
  impressions: number;
}

/**
 * Fetch top pages (by clicks) from backend GSC endpoint
 * @param brandId Brand ID
 * @param startDate YYYY-MM-DD
 * @param endDate YYYY-MM-DD
 * @returns Array of GSCTopPage
 */
export async function fetchGSCTopPages(
  brandId: string,
  startDate: string,
  endDate: string,
): Promise<GSCTopPage[]> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/top-pages`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId, startDate, endDate }),
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch GSC Top Pages");
  }
  return res.json();
}

// --- Rate limiting: block backend calls after first failure until page refresh ---
let GSC_BACKEND_BLOCKED = false;

function checkGSCBackendBlocked() {
  if (GSC_BACKEND_BLOCKED) {
    throw new Error(
      "GSC backend is rate limited due to previous failure. Reload the page to try again.",
    );
  }
}

// Initiate GSC OAuth flow via backend, opening popup for user
export async function initiateGSCAuth(
  brandId: string,
  category: string,
): Promise<void> {
  const url = `${pb.baseURL}/api/v1/gsc/oauth/initiate?brandId=${encodeURIComponent(brandId)}&category=${encodeURIComponent(category)}`;
  const res = await fetch(url, {
    headers: {
      Authorization: pb.authStore.token,
    },
  });
  const data = await res.json();
  console.log("error response", data);
  if (!data.authUrl) throw new Error("Failed to get Google OAuth URL");
  // Open popup
  const popup = window.open(data.authUrl, "oauthGSC", "width=500,height=600");
  if (!popup) throw new Error("Failed to open popup window");
  // Poll for popup close
  return new Promise((resolve) => {
    const timer = window.setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        resolve();
      }
    }, 700);
  });
}

// Fetch indexed pages from backend
export async function fetchGSCIndexedPages(
  brandId: string,
): Promise<IndexedPagesResponse> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/indexed-pages`;
  console.log("[GSC] fetchGSCIndexedPages request", { url, brandId });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId }),
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch indexed pages");
  }
  return res.json();
}

// Fetch backlinks from backend
export async function fetchGSCBacklinks(
  brandId: string,
): Promise<BacklinkData[]> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/backlinks`;
  console.log("[GSC] fetchGSCBacklinks request", { url, brandId });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId }),
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch backlinks");
  }
  return res.json();
}

// Service to fetch Google Search Console performance metrics
export async function fetchGSCPerformance(
  brandId: string,
  startDate: string,
  endDate: string,
  signal?: AbortSignal,
): Promise<GSCPerformanceResponse> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/performance`;
  console.log("[GSC] fetchGSCPerformance request", {
    url,
    brandId,
    startDate,
    endDate,
  });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId, startDate, endDate }),
    signal,
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch GSC performance");
  }
  return res.json();
}

export interface GSCQueryData {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

// Service to fetch top queries from Google Search Console
export async function fetchGSCQueries(
  brandId: string,
  startDate: string,
  endDate: string,
): Promise<GSCQueryData[]> {
  checkGSCBackendBlocked();
  const url = `${pb.baseURL}/api/v1/gsc/queries`;
  console.log("[GSC] fetchGSCQueries request", {
    url,
    brandId,
    startDate,
    endDate,
  });
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: pb.authStore.token,
    },
    body: JSON.stringify({ brandId, startDate, endDate }),
  });
  if (!res.ok) {
    GSC_BACKEND_BLOCKED = true;
    throw new Error("Failed to fetch GSC queries");
  }
  return res.json();
}
